/* /species/:speciesId — Conservation species detail page.
 *
 * Consumes two backend endpoints:
 *   GET /api/species/{id}                — base species doc
 *   GET /api/species/{id}/conservation   — hybrid IUCN + AI narrative payload
 *
 * Renders a warm, factually-cautious card the community can share. Missing
 * fields collapse silently — this is important because the AI narrative and
 * the IUCN API can each be unavailable independently.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Leaf, Shield, HeartHandshake, Info, ExternalLink, Star } from 'lucide-react';
import api from '../lib/api';
import { Skeleton } from '../components/Skeleton';

const IUCN_META = {
  LC: { label: 'Least Concern',         color: '#22c55e', ring: 'ring-emerald-400/40', bg: 'bg-emerald-500/10' },
  NT: { label: 'Near Threatened',       color: '#84cc16', ring: 'ring-lime-400/40',    bg: 'bg-lime-500/10' },
  VU: { label: 'Vulnerable',            color: '#eab308', ring: 'ring-yellow-400/40',  bg: 'bg-yellow-500/10' },
  EN: { label: 'Endangered',            color: '#f97316', ring: 'ring-orange-400/40',  bg: 'bg-orange-500/10' },
  CR: { label: 'Critically Endangered', color: '#ef4444', ring: 'ring-red-400/50',     bg: 'bg-red-500/10' },
  EW: { label: 'Extinct in the Wild',   color: '#a855f7', ring: 'ring-fuchsia-400/40', bg: 'bg-fuchsia-500/10' },
  EX: { label: 'Extinct',               color: '#525252', ring: 'ring-neutral-500/40', bg: 'bg-neutral-500/10' },
  DD: { label: 'Data Deficient',        color: '#94a3b8', ring: 'ring-slate-400/40',   bg: 'bg-slate-500/10' },
  NE: { label: 'Not Evaluated',         color: '#94a3b8', ring: 'ring-slate-400/40',   bg: 'bg-slate-500/10' },
};

function StatusPill({ code }) {
  const meta = IUCN_META[code] || IUCN_META.NE;
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ring-1 ${meta.ring} ${meta.bg}`}
         data-testid="species-detail-status">
      <span className="w-2 h-2 rounded-full" style={{ background: meta.color, boxShadow: `0 0 8px ${meta.color}` }} />
      <span className="text-[11px] uppercase tracking-widest" style={{ color: meta.color }}>{meta.label}</span>
      <span className="text-[9px] font-mono text-white/60">{code}</span>
    </div>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  if (!children) return null;
  return (
    <div className="rounded-3xl glass border border-white/10 p-4 space-y-2" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-accent" />
        <p className="text-[10px] uppercase tracking-widest text-accent">{title}</p>
      </div>
      <div className="text-sm text-white/85 leading-relaxed">{children}</div>
    </div>
  );
}

export default function SpeciesDetail() {
  const nav = useNavigate();
  const { speciesId } = useParams();
  const [species, setSpecies] = useState(null);
  const [conservation, setConservation] = useState(null);
  const [ecology, setEcology] = useState(null);
  const [state, setState] = useState('loading');   // loading | ready | not_found

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, c, e] = await Promise.all([
          api.get(`/species/${speciesId}`),
          api.get(`/species/${speciesId}/conservation`).catch(() => ({ data: null })),
          api.get(`/species/${speciesId}/ecology`).catch(() => ({ data: { roles: [] } })),
        ]);
        if (cancelled) return;
        setSpecies(s.data);
        setConservation(c.data);
        setEcology(e.data);
        setState('ready');
      } catch (err) {
        if (!cancelled) setState('not_found');
      }
    })();
    return () => { cancelled = true; };
  }, [speciesId]);

  if (state === 'loading') {
    return (
      <div className="space-y-4 py-4" data-testid="species-detail-loading">
        <Skeleton className="h-40 rounded-3xl" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-2/3 rounded" />
          <Skeleton className="h-3 w-1/2 rounded opacity-70" />
        </div>
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-24 rounded-2xl" />
      </div>
    );
  }
  if (state === 'not_found') {
    return (
      <div className="text-center py-16 space-y-3" data-testid="species-detail-not-found">
        <p className="font-serif text-2xl">Species not found</p>
        <p className="text-sm text-white/60">This trail's gone cold.</p>
        <button onClick={() => nav(-1)} className="px-4 py-2 rounded-full bg-white/10 text-[11px] uppercase tracking-widest">Go back</button>
      </div>
    );
  }

  const iucn = conservation?.iucn || {};
  const narrative = conservation?.narrative || {};
  const code = (iucn.category || 'NE').toUpperCase();

  return (
    <div className="space-y-5 pb-24" data-testid="species-detail-root">
      <div className="flex items-center gap-2">
        <button onClick={() => nav(-1)} data-testid="species-detail-back"
          className="p-1.5 rounded-full glass border border-white/10">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-accent">Field guide</p>
          <h1 className="font-serif text-3xl leading-tight truncate" data-testid="species-detail-name">
            {species?.common_name || species?.scientific_name || 'Unknown species'}
          </h1>
          {species?.scientific_name && (
            <p className="italic text-sm text-white/60 truncate">{species.scientific_name}</p>
          )}
          {species?.first_captor_nickname && (
            <p className="mt-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-200 bg-amber-500/10 border border-amber-300/30 rounded-full px-2 py-0.5"
               data-testid="species-first-captor">
              <Star className="w-3 h-3" fill="currentColor" strokeWidth={0} />
              First discovered by <span className="font-semibold ml-0.5">@{species.first_captor_nickname}</span>
            </p>
          )}
        </div>
      </div>

      {/* Hero image (if any) */}
      {species?.image_url && (
        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
          className="relative aspect-[16/10] rounded-3xl overflow-hidden border border-white/10">
          <img src={species.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/85 to-transparent" />
          <div className="absolute left-4 bottom-4"><StatusPill code={code} /></div>
        </motion.div>
      )}

      {/* Status row (if no image) */}
      {!species?.image_url && <StatusPill code={code} />}

      {/* Population trend */}
      {iucn.population_trend && (
        <div className="rounded-2xl px-3 py-2 bg-white/5 border border-white/10 text-[11px] uppercase tracking-widest text-white/70"
             data-testid="species-detail-trend">
          Population trend · <span className="text-accent">{iucn.population_trend}</span>
        </div>
      )}

      {/* Ecological Roles — iter39 educational metadata */}
      {(ecology?.roles?.length || 0) > 0 && (
        <div className="rounded-3xl glass border border-white/10 p-4 space-y-2" data-testid="species-detail-ecology">
          <p className="text-[10px] uppercase tracking-widest text-accent">Ecological role</p>
          <div className="flex flex-wrap gap-1.5">
            {ecology.roles.map(role => (
              <span
                key={role.id}
                data-testid={`ecology-role-${role.id}`}
                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] border"
                style={{ borderColor: `${role.color}66`, background: `${role.color}22`, color: role.color }}
                title={role.desc}
              >
                {role.label}
              </span>
            ))}
          </div>
          <p className="text-[11px] text-white/60 leading-relaxed">
            {ecology.roles[0]?.desc}
          </p>
        </div>
      )}

      {/* AI narrative sections */}
      <Section testid="species-detail-habitat" icon={Leaf} title="Habitat">{narrative.habitat}</Section>
      <Section testid="species-detail-diet" icon={Leaf} title="Diet">{narrative.diet}</Section>
      <Section testid="species-detail-behaviour" icon={Info} title="Behaviour">{narrative.behaviour}</Section>
      <Section testid="species-detail-why" icon={HeartHandshake} title="Why it matters">{narrative.why_matters}</Section>
      <Section testid="species-detail-threats" icon={Shield} title="Threats">{narrative.threats_summary}</Section>
      <Section testid="species-detail-tips" icon={HeartHandshake} title="How you can help">{narrative.conservation_tips}</Section>

      {/* Structured IUCN lists */}
      {(iucn.primary_threats?.length || 0) > 0 && (
        <div className="rounded-3xl glass border border-white/10 p-4 space-y-2" data-testid="species-detail-threat-list">
          <p className="text-[10px] uppercase tracking-widest text-accent">Documented threats</p>
          <ul className="flex flex-wrap gap-1.5">
            {iucn.primary_threats.map((t, i) => (
              <li key={i} className="text-[11px] px-2 py-1 rounded-full bg-red-500/10 border border-red-400/30 text-red-200">
                {t.label || t.code}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(iucn.main_habitats?.length || 0) > 0 && (
        <div className="rounded-3xl glass border border-white/10 p-4 space-y-2" data-testid="species-detail-habitat-list">
          <p className="text-[10px] uppercase tracking-widest text-accent">Main habitats</p>
          <ul className="flex flex-wrap gap-1.5">
            {iucn.main_habitats.map((h, i) => (
              <li key={i} className="text-[11px] px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-emerald-200">
                {h.label || h.code}
              </li>
            ))}
          </ul>
        </div>
      )}

      {iucn.range_summary && (
        <div className="rounded-3xl glass border border-white/10 p-4 space-y-2" data-testid="species-detail-range">
          <p className="text-[10px] uppercase tracking-widest text-accent">Range</p>
          <p className="text-sm text-white/85 leading-relaxed">{iucn.range_summary}</p>
        </div>
      )}

      {/* Provenance footer */}
      <div className="pt-2 text-[10px] uppercase tracking-widest text-white/50 flex flex-wrap items-center gap-2"
           data-testid="species-detail-provenance">
        {iucn.source === 'iucn' ? (
          <a
            href={`https://www.iucnredlist.org/species/${iucn.assessment_id || ''}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 hover:text-white">
            IUCN Red List <ExternalLink className="w-3 h-3" />
          </a>
        ) : (
          <span>IUCN status is data-deficient for this species.</span>
        )}
        {narrative.source === 'gpt-5.2' && <span>· Narrative by GPT-5.2</span>}
      </div>
    </div>
  );
}
